import {computed, Injectable, signal} from '@angular/core';
import {Router} from '@angular/router';
import {
  ItineraryItemDetails,
  TaTemplateDetails,
  TaTemplateDetailsRestResponse,
  TaTemplateSummary,
  TemplateService,
  TravellerDetails,
  UpdateTemplateFieldRequest,
  UserTravelItinerary,
  UserTravelItineraryRestResponse,
} from '../api';
import {TaAppStore} from '../state/clcta-store';
import {QueueService} from '../services/queue.service';
import {LoadingService} from '../services/loading.service';
import {ErrorMessageService} from '../services/error-message.service';
import {TaSubmissionDatasource} from './ta-submission.datasource';
import {toLocalISOString} from '../util/date.utils';
import {CreateTaStepType} from '../util/app.types';
import {EMPTY, Observable} from 'rxjs';

export type TemplateStepCode =
  CreateTaStepType.TRAVELLERS | CreateTaStepType.DETAILS | CreateTaStepType.ITINERARY | CreateTaStepType.REVIEW;

export const TEMPLATE_STEPS: {code: TemplateStepCode; title: string; subtitle: string}[] = [
  {code: CreateTaStepType.TRAVELLERS, title: "Who's Travelling", subtitle: "Who's Travelling on this TA"},
  {code: CreateTaStepType.DETAILS, title: 'Travel Details', subtitle: 'Travel Details'},
  {code: CreateTaStepType.ITINERARY, title: 'Itinerary', subtitle: 'Your Travel Itinerary'},
  {code: CreateTaStepType.REVIEW, title: 'Review', subtitle: 'Review Template'},
];

/** Items in day order. The server returns them unordered. */
export function sortedItems(itinerary: UserTravelItinerary | undefined): ItineraryItemDetails[] {
  return [...(itinerary?.items ?? [])].sort((a, b) => (a.start ?? '').localeCompare(b.start ?? ''));
}

/** The itinerary a template traveller (UserTravel id) is on, from the template's own list. */
export function itineraryForTraveller(template: TaTemplateDetails | undefined, travellerId: string | null | undefined)
  : UserTravelItinerary | undefined {
  if (!template || !travellerId) return undefined;
  return (template.itineraries ?? []).find(itinerary =>
    itinerary.userTravelId === travellerId || (itinerary.linkedUserTravellers ?? []).includes(travellerId));
}

export function primaryTravellerId(template: TaTemplateDetails | undefined): string | undefined {
  const primaryUserId = template?.primaryTraveller?.id;
  return template?.travellers?.find(t => !!primaryUserId && t.details?.id === primaryUserId)?.travellerId ?? undefined;
}

function shiftDays(iso: string | null | undefined, days: number): string | null | undefined {
  if (!iso) return iso;
  const date = new Date(iso);
  date.setDate(date.getDate() + days);
  return toLocalISOString(date);
}

/**
 * Personal TA templates: list, wizard edits and raising a TA from one. Template state is held here
 * rather than in TaAppStore so it never mixes with the current TA. Saves go through the queue, as
 * TaSubmissionDatasource's do.
 */
@Injectable({
  providedIn: 'root'
})
export class TemplateDatasource {
  templates = signal<TaTemplateSummary[]>([]);
  template = signal<TaTemplateDetails | undefined>(undefined);
  // The itinerary shown on the editable Itinerary step.
  currentItinerary = signal<UserTravelItinerary | undefined>(undefined);
  isLoadingItinerary = signal<boolean>(false);
  hasTemplates = computed<boolean>(() => this.templates().length > 0);

  stepValid = computed<Record<TemplateStepCode, boolean>>(() => {
    const template = this.template();
    const itineraries = template?.itineraries ?? [];
    return {
      [CreateTaStepType.TRAVELLERS]: (template?.travellers?.length ?? 0) > 0 && !!template?.primaryTraveller?.id,
      [CreateTaStepType.DETAILS]: !!template?.project?.id && !!template?.purpose,
      [CreateTaStepType.ITINERARY]: itineraries.length > 0 && itineraries.every(itinerary =>
        (itinerary.items?.length ?? 0) > 0
        && itinerary.items!.every(item => !!item.startLocationId && !!item.endLocationId)),
      [CreateTaStepType.REVIEW]: !!template?.name?.trim(),
    };
  });

  constructor(
    private templateApi: TemplateService,
    private store: TaAppStore,
    private taSubmissionDatasource: TaSubmissionDatasource,
    private queue: QueueService,
    private loadingService: LoadingService,
    private errorMessageService: ErrorMessageService,
    private router: Router,
  ) {}

  ////////////////////////////////////////////////
  // Templates
  ////////////////////////////////////////////////

  loadTemplates(): void {
    this.templateApi.listTATemplates().subscribe({
      next: response => {
        if (response.success) {
          this.templates.set(response.data ?? []);
        } else {
          console.error('listTATemplates failed', response.error);
        }
      },
      error: error => console.error('listTATemplates error', error),
    });
  }

  /**
   * Loads the template unless it is already the current one; `force` refetches it, keeping the
   * current copy on screen meanwhile. Resolves false when it cannot be loaded.
   */
  loadTemplate(id: string, force = false): Promise<boolean> {
    if (this.template()?.id === id && !force) {
      return Promise.resolve(true);
    }
    if (this.template()?.id !== id) {
      this.template.set(undefined);
      this.currentItinerary.set(undefined);
    }
    return new Promise(resolve => {
      this.templateApi.getTATemplate(id).subscribe({
        next: response => {
          if (response.success && response.data) {
            // A save queued meanwhile returns a newer copy; this one would undo its optimistic write.
            if (!(this.queue.isSaving() && this.template()?.id === id)) {
              this.template.set(response.data);
            }
            resolve(true);
          } else {
            console.error('getTATemplate failed', response.error);
            resolve(false);
          }
        },
        error: error => {
          console.error('getTATemplate error', error);
          resolve(false);
        },
      });
    });
  }

  /** Creates an empty template and opens it on the first step. Resolves null on success, else the error to show. */
  createTemplate(name: string): Promise<string | null> {
    const fallback = 'Error creating template.';
    this.loadingService.showLoading({message: 'Creating Template...', spinnerSize: 'md'});
    return new Promise(resolve => {
      this.templateApi.createTATemplate({name}).subscribe({
        next: response => {
          this.loadingService.hideLoading();
          if (response.success && response.data?.id) {
            this.template.set(response.data);
            this.currentItinerary.set(undefined);
            this.loadTemplates();
            this.router.navigate(['/template', response.data.id], {queryParams: {step: CreateTaStepType.TRAVELLERS}});
            resolve(null);
          } else {
            resolve(response.error ?? fallback);
          }
        },
        error: err => {
          this.loadingService.hideLoading();
          console.error('createTATemplate error', err);
          resolve(err?.error?.error ?? fallback);
        },
      });
    });
  }

  /** Saves a TA as a template and opens it on the first step. Resolves null on success, else the error to show. */
  createTemplateFromTa(submissionId: string, name: string): Promise<string | null> {
    const fallback = 'Error creating template.';
    this.loadingService.showLoading({message: 'Creating Template...', spinnerSize: 'md'});
    return new Promise(resolve => {
      this.templateApi.createTATemplateFromSubmission({submissionId, name}).subscribe({
        next: response => {
          this.loadingService.hideLoading();
          if (response.success && response.data?.id) {
            this.template.set(response.data);
            this.currentItinerary.set(undefined);
            this.loadTemplates();
            this.router.navigate(['/template', response.data.id], {queryParams: {step: CreateTaStepType.TRAVELLERS}});
            resolve(null);
          } else {
            resolve(response.error ?? fallback);
          }
        },
        error: err => {
          this.loadingService.hideLoading();
          console.error('createTATemplateFromSubmission error', err);
          resolve(err?.error?.error ?? fallback);
        },
      });
    });
  }

  /** Not queued: callers gate on store.isSavingData() so it cannot overtake a save. */
  deleteTemplate(id: string): Promise<boolean> {
    return new Promise(resolve => {
      this.templateApi.deleteTATemplate(id).subscribe({
        next: response => {
          if (response.success) {
            this.templates.update(list => list.filter(t => t.id !== id));
            if (this.template()?.id === id) {
              this.template.set(undefined);
              this.currentItinerary.set(undefined);
            }
            resolve(true);
          } else {
            this.errorMessageService.showTemporaryErrorWithTitle('Delete Failed', response.error ?? '', 10000);
            resolve(false);
          }
        },
        error: error => {
          console.error('deleteTATemplate error', error);
          this.errorMessageService.showTemporaryError('Error deleting template.', 5000);
          resolve(false);
        },
      });
    });
  }

  /** Raises an InProgress TA from the template and opens it in the create wizard. */
  createTaFromTemplate(templateId: string, startDate: Date, forSelf: boolean): void {
    this.store.updatePreviousPageRoute(this.router.url);
    this.loadingService.showLoading({message: 'Creating Travel Allowance...', spinnerSize: 'md'});
    this.templateApi.createTAFromTemplate({templateId, startDate: toLocalISOString(startDate), forSelf}).subscribe({
      next: response => {
        this.loadingService.hideLoading();
        if (response.success && response.data?.id) {
          this.store.updateTaSubmission(response.data);
          // Same cache drops as createServerTaSubmission: the new TA is in no cached list yet.
          this.store.resetHistoryCache();
          if (response.data.delegate) {
            this.store.resetDelegateCache('active');
          }
          this.taSubmissionDatasource.calculateAllStepsValid();
          this.router.navigate(['/ta/' + response.data.id]);
        } else {
          this.errorMessageService.showTemporaryErrorWithTitle('Create Failed', response.error ?? '', 10000);
        }
      },
      error: error => {
        this.loadingService.hideLoading();
        console.error('createTAFromTemplate error', error);
        this.errorMessageService.showTemporaryError('Error creating TA from template.', 5000);
      },
    });
  }

  ////////////////////////////////////////////////
  // Template fields
  ////////////////////////////////////////////////

  /**
   * Saves one of name, projectId, purpose, purposeNotes or primaryTraveller. `local` is written
   * optimistically; a refused save reloads the template to undo it. Resolves whether it saved.
   */
  saveField(fieldName: string, value: string | null | undefined, local: Partial<TaTemplateDetails>): Promise<boolean> {
    this.patchTemplate(local);
    return this.enqueueFieldUpdate({templateId: this.template()!.id!, fieldName, value: value ?? null}, fieldName);
  }

  /** Replaces the traveller list. The server re-derives the primary when it leaves. */
  saveTravellers(travellers: TravellerDetails[]): Promise<boolean> {
    this.patchTemplate({travellers});
    return this.enqueueFieldUpdate({templateId: this.template()!.id!, fieldName: 'travellers', travellers}, 'travellers')
      .then(saved => {
        // A removed traveller's itinerary may be gone; show the step's fallback instead.
        const current = this.currentItinerary();
        if (current && !this.template()?.travellers?.some(t => t.travellerId === current.userTravelId)) {
          this.currentItinerary.set(undefined);
        }
        return saved;
      });
  }

  private enqueueFieldUpdate(request: UpdateTemplateFieldRequest, label: string): Promise<boolean> {
    const templateId = request.templateId!;
    return new Promise(resolve => {
      this.queue.enqueue(() => this.templateApi.updateTATemplateField(request)).subscribe({
        next: (response: TaTemplateDetailsRestResponse) => {
          if (response.success && response.data) {
            if (this.template()?.id === templateId) {
              this.template.set(response.data);
            }
            resolve(true);
          } else {
            this.errorMessageService.showTemporaryError(response.error ?? 'Updating template property failed: ' + label, 5000);
            this.reloadTemplate(templateId);
            resolve(false);
          }
        },
        error: error => {
          console.error('updateTATemplateField error', error);
          this.errorMessageService.showTemporaryError('Error updating template property: ' + label, 5000);
          this.reloadTemplate(templateId);
          resolve(false);
        },
      });
    });
  }

  private patchTemplate(local: Partial<TaTemplateDetails>): void {
    this.template.update(template => template ? {...template, ...local} : template);
  }

  private reloadTemplate(id: string): void {
    if (this.template()?.id !== id) return;
    this.templateApi.getTATemplate(id).subscribe({
      next: response => {
        if (response.success && response.data && this.template()?.id === id) {
          this.template.set(response.data);
        }
      },
      error: error => console.error('getTATemplate error', error),
    });
  }

  ////////////////////////////////////////////////
  // Itinerary
  ////////////////////////////////////////////////

  /** Not queued: the traveller tabs that trigger it are disabled while a save is outstanding. */
  loadTravellerItinerary(travellerId: string): void {
    this.isLoadingItinerary.set(true);
    this.templateApi.getTATemplateItineraryForTraveller(travellerId).subscribe({
      next: response => {
        this.isLoadingItinerary.set(false);
        if (response.success && response.data) {
          this.currentItinerary.set(response.data);
          this.syncTemplateItinerary(response.data);
        } else {
          this.errorMessageService.showTemporaryError('Failed to get itinerary for traveller: ' + response.error, 5000);
        }
      },
      error: error => {
        this.isLoadingItinerary.set(false);
        console.error('getTATemplateItineraryForTraveller error', error);
      },
    });
  }

  saveItineraryItem(item: ItineraryItemDetails): void {
    this.patchCurrentItinerary(items => items.map(i => i.id === item.id ? item : i));
    this.enqueueItinerary(() => this.templateApi.updateTATemplateItineraryItem(this.withTraveller(item)), 'saving itinerary day');
  }

  /**
   * Appends the day after the last one, at the same times. The last day is read when the task
   * runs, not on click, so rapid clicks each see the previous add's response.
   */
  addItineraryDay(): void {
    const itineraryId = this.currentItinerary()?.id;
    if (!itineraryId) return;
    this.enqueueItinerary(() => {
      const current = this.currentItinerary();
      // Traveller switched while queued: drop the add. EMPTY still completes, so the queue count drops.
      if (current?.id !== itineraryId) return EMPTY;
      const last = sortedItems(current).at(-1);
      // With no days left, fall back to the server's day 0 for a fresh template (TemplateStore.Anchor).
      const start = last?.start ? shiftDays(last.start, 1) : toLocalISOString(new Date(2001, 0, 1, 8, 30));
      const end = last?.start ? shiftDays(last.end ?? last.start, 1) : toLocalISOString(new Date(2001, 0, 1, 16, 30));
      return this.templateApi.updateTATemplateItineraryItem(this.withTraveller({itineraryId, start, end}));
    }, 'adding itinerary day');
  }

  /**
   * Deletes the day and moves every later day back one, so a TA raised from the template has no
   * gap in its dates.
   */
  removeItineraryDay(item: ItineraryItemDetails): void {
    const current = this.currentItinerary();
    if (!current?.id || !item.id) return;
    const later = sortedItems(current)
      .filter(i => i.id !== item.id && (i.start ?? '') > (item.start ?? ''))
      .map(i => ({...i, start: shiftDays(i.start, -1), end: shiftDays(i.end, -1)}));

    this.patchCurrentItinerary(items => items
      .filter(i => i.id !== item.id)
      .map(i => later.find(l => l.id === i.id) ?? i));

    // Only the last response is shown: an earlier one would put the unshifted dates back on screen,
    // and an edit made from them would undo the shift.
    const deleteItem = this.withTraveller(item);
    this.enqueueItinerary(() => this.templateApi.deleteTATemplateItineraryItem(deleteItem),
      'removing itinerary day', later.length === 0);
    later.forEach((shifted, index) => {
      const shiftedItem = this.withTraveller(shifted);
      this.enqueueItinerary(() => this.templateApi.updateTATemplateItineraryItem(shiftedItem),
        'moving itinerary day', index === later.length - 1);
    });
  }

  saveItineraryNotes(notes: string): void {
    const current = this.currentItinerary();
    if (!current?.id) return;
    this.currentItinerary.set({...current, notes});
    this.enqueueItinerary(() => this.templateApi.updateTATemplateItinerary({...current, notes}), 'saving itinerary notes');
  }

  private withTraveller(item: ItineraryItemDetails): ItineraryItemDetails {
    // The server maps its response for this UserTravel.
    return {...item, travellerId: this.currentItinerary()?.userTravelId ?? undefined};
  }

  private patchCurrentItinerary(update: (items: ItineraryItemDetails[]) => ItineraryItemDetails[]): void {
    this.currentItinerary.update(itinerary => itinerary ? {...itinerary, items: update(itinerary.items ?? [])} : itinerary);
  }

  private enqueueItinerary(task: () => Observable<UserTravelItineraryRestResponse>, label: string, show = true): void {
    this.queue.enqueue(task).subscribe({
      next: response => {
        if (response.success && response.data) {
          if (show && this.currentItinerary()?.id === response.data.id) {
            this.currentItinerary.set(response.data);
          }
          this.syncTemplateItinerary(response.data);
        } else {
          this.errorMessageService.showTemporaryError(`Error ${label}: ${response.error ?? ''}`, 5000);
          this.reloadCurrentItinerary();
        }
      },
      error: error => {
        console.error(label + ' error', error);
        this.errorMessageService.showTemporaryError(`Error ${label}.`, 5000);
        this.reloadCurrentItinerary();
      },
    });
  }

  private reloadCurrentItinerary(): void {
    const travellerId = this.currentItinerary()?.userTravelId;
    if (travellerId) {
      this.loadTravellerItinerary(travellerId);
    }
  }

  // Keeps template.itineraries current, which the review step and step validity read.
  private syncTemplateItinerary(itinerary: UserTravelItinerary): void {
    this.template.update(template => {
      if (!template || (!!itinerary.templateId && itinerary.templateId !== template.id)) return template;
      const others = (template.itineraries ?? []).filter(i => i.id !== itinerary.id);
      return {...template, itineraries: [...others, itinerary]};
    });
  }
}
